/**
 * iCloud 基础/高级绑定状态（前端侧）
 * - Basic=1：基础绑定成功
 * - 高级绑定是否完成：看 resetTime.advancedDownloadTime
 *   （来自 /api/definition/user-device-reset-time 的 advanced_download_time，验证成功开始下载即写入）
 * - Updated 展示仍用 resetTime.advancedUpdateDownloadTime（USER_SUMMARY 下载完成时间）
 *
 * resetTime 字段约定：
 * - null：尚未拉取（刷新/首屏）→ 不得当成「未验证」去闪验证页
 * - ''：已拉取且确认未完成
 * - 时间字符串：已完成
 */

import resolveCurrentDevicePlatformType from 'src/utils/resolveCurrentDevicePlatformType'

/** 侧栏 / Dashboard Extreme 功能路径 */
export const IOS_EXTREME_FEATURE_PATHS = [
  '/text-messages',
  '/calls',
  '/voicemail',
  '/whatsapp',
  '/wa-business',
  '/line',
  '/kik',
  '/we-chat',
  '/messenger',
  '/browser-history',
  '/browser-bookmark',
  '/apps',
  '/wifi-networks',
  '/skype',
  '/teams',
  '/telegram',
  '/snapchat',
  '/instagram',
  '/facetime',
]

export function isIcloudBasicBound() {
  if (typeof window === 'undefined') return false

  return String(window.localStorage.getItem('Basic')) === '1'
}

export function getBoundIcloudUsername() {
  if (typeof window === 'undefined') return ''

  try {
    return JSON.parse(window.localStorage.getItem('DeviceUser') || '{}')?.username || ''
  } catch {
    return ''
  }
}

/** resetTime 是否仍在首屏拉取中（null = 未就绪） */
export function isResetTimePending(resetTime) {
  if (!resetTime) return true

  return resetTime.basicUpdateDownloadTime == null || resetTime.advancedDownloadTime == null
}

/** 未完成基础验证（仅已拉取且为空时为 true；pending 时为 false） */
export function isBasicVerificationIncomplete(resetTime) {
  if (isResetTimePending(resetTime)) return false

  return resetTime.basicUpdateDownloadTime === ''
}

/**
 * （历史）功能页曾用 IosExtreme 遮罩判基础步骤；现功能页只出升级弹窗 / 跳转补充绑定页。
 * 仍可供其它入口复用：已 Basic=1 则不再强制基础验证。
 */
export function shouldForceBasicVerificationOverlay(resetTime, basicVerfiyOver) {
  if (isIcloudBasicBound()) return false
  if ([true, 'true'].includes(basicVerfiyOver)) return false

  return isBasicVerificationIncomplete(resetTime)
}

/** 基础已满足：Basic=1 / redux 已过 / 下载完成时间已有 */
export function isBasicVerificationSatisfiedForOverlay(resetTime, basicVerfiyOver) {
  return !shouldForceBasicVerificationOverlay(resetTime, basicVerfiyOver)
}

/**
 * 是否仍缺「基础」步骤（已 Basic=1 则否）。
 * 功能页门控已改为 useExtremeFeatureAccessGate；此函数仍用于 ios-verfiy-process 等。
 */
export function isBasicStepStillRequired(resetTime) {
  if (isIcloudBasicBound()) return false

  return isBasicVerificationIncomplete(resetTime)
}

/**
 * 未完成高级绑定。
 * @param {object|string|null|undefined} resetTimeOrValue
 *   - 传 resetTime 对象：读 advancedDownloadTime
 *   - 传字符串：兼容直接传时间值
 * pending（null）时返回 false，避免刷新闪验证/误跳转
 */
export function isAdvancedVerificationIncomplete(resetTimeOrValue) {
  if (resetTimeOrValue != null && typeof resetTimeOrValue === 'object') {
    if (isResetTimePending(resetTimeOrValue)) return false

    return resetTimeOrValue.advancedDownloadTime === ''
  }

  if (resetTimeOrValue == null) return false

  return resetTimeOrValue === ''
}

export function isIosExtremeFeaturePath(path) {
  if (!path) return false

  return IOS_EXTREME_FEATURE_PATHS.includes(path)
}

/**
 * 解析用于 Extreme 门控的平台：优先 user-device.platform_type，再 currentMode。
 * @param {string} [mode]
 * @returns {string}
 */
export function resolveExtremeGatePlatform(mode) {
  const fromDevice =
    typeof window !== 'undefined' ? resolveCurrentDevicePlatformType() : null

  return (
    fromDevice ||
    mode ||
    (typeof window !== 'undefined' ? window.localStorage.getItem('currentMode') : '') ||
    ''
  )
}

/** 是否 Android 平台（无 iCloud 基础/高级绑定概念） */
export function isAndroidPlatformMode(mode) {
  return String(resolveExtremeGatePlatform(mode)).startsWith('Android')
}

/** 是否 iOS-Cloud（唯一有基础/高级绑定的平台） */
export function isIosCloudMode(mode) {
  return resolveExtremeGatePlatform(mode) === 'iOS-Cloud'
}

/** 是否启用 iOS 高级绑定门控（非 iOS-Cloud 恒为 false） */
export function shouldApplyIosExtremeBindGate(mode) {
  if (typeof window !== 'undefined') {
    const path = window.location?.pathname || ''
    if (path.startsWith('/android')) return false
  }

  if (isAndroidPlatformMode(mode)) return false

  return isIosCloudMode(mode)
}
